import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Item de orçamento (`.spec/010`, "Item de Orçamento"; `.spec/015`, decisão 58).
 *
 * É o **vocabulário do comercial**: as linhas pelas quais a construtora orça uma
 * obra antes de ela existir — "Fundação", "Alvenaria de vedação", "Projeto
 * estrutural". O orçamento é montado com elas.
 *
 * **Não é o insumo, e a distinção é o motivo de a tabela existir.** O insumo é o
 * que se compra do fornecedor, com marca, bitola e NCM; o item de orçamento é o
 * que se vende ao cliente, e uma linha dele reúne dezenas de insumos. Fundi-los
 * numa tabela só obrigaria o catálogo de compras a carregar linhas que nunca
 * entram em nota, e o orçamento a escolher entre "Cimento CP-II 50kg" e "Tubo
 * PVC 25mm" onde ele quer dizer "Alvenaria".
 *
 * **Não há vínculo com o insumo, e a ausência é decisão** (`.spec/015`, decisão
 * 58, revista): o insumo chegou a apontar para uma linha do catálogo, para
 * comparar orçado com realizado. O campo saiu porque o mesmo insumo entra em
 * mais de uma linha do orçamento — cimento está na fundação e na alvenaria —, e
 * um ponteiro único no cadastro do insumo obrigaria a escolher uma delas, o que
 * faz a comparação mentir em vez de faltar. O confronto orçado × realizado, se
 * vier, sai do rateio do documento, que já é por obra e por linha, e não do
 * cadastro.
 */

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

/** O teto do código, espelhado no `CHECK` e no `varchar` da coluna. */
export const TAMANHO_MAXIMO_CODIGO_ITEM_ORCAMENTO = 20;

/**
 * Código alfanumérico, em caixa alta, **informado pelo usuário**.
 *
 * Ao contrário de `insumos.codigo` (`.spec/015`, decisão 49), ele **não é
 * sugerido**: o catálogo de itens de orçamento é curto — dezenas de linhas, não
 * centenas — e a codificação dele costuma vir da planilha que o orçamentista já
 * usa, com sigla de etapa ("FUND-01", "ALV-02"). Um sequencial sugerido seria
 * apagado em toda gravação.
 *
 * **Normalizado para caixa alta, e o espaço é recusado.** A diferença entre os
 * dois tratamentos é a mesma do insumo: a caixa tem correção óbvia — "fund-01" e
 * "FUND-01" são o mesmo código, e ninguém discorda —, e o espaço não: "FUND 01"
 * pode ser o que a pessoa quis, e transformá-lo em "FUND01" em silêncio criaria
 * um código que ela não reconhece na listagem.
 */
const codigoSchema = z
  .string()
  .trim()
  .min(1, 'Informe o código do item de orçamento.')
  .max(
    TAMANHO_MAXIMO_CODIGO_ITEM_ORCAMENTO,
    `O código tem no máximo ${TAMANHO_MAXIMO_CODIGO_ITEM_ORCAMENTO} caracteres.`,
  )
  .regex(
    /^[^\s]+$/,
    'O código não aceita espaço. Use hífen, ponto ou barra para separar — "FUND-01".',
  )
  .transform((valor) => valor.toUpperCase());

const nomeSchema = z
  .string()
  .trim()
  .min(2, 'Informe o nome do item de orçamento.')
  .max(200, 'O nome tem no máximo 200 caracteres.');

/**
 * Payload de gravação.
 *
 * **Sem `ordem` e sem pai.** O catálogo é plano nesta versão, e a hierarquia —
 * "1. Serviços preliminares" contendo "1.1 Limpeza do terreno" — foi avaliada e
 * deixada de fora: ela muda a listagem, o seletor e a soma do orçamento, e nada
 * no pedido exige que exista agora. O código já ordena quando o orçamentista o
 * numera, que é o que ele faz na planilha de onde o catálogo vem.
 *
 * **Sem `tipo`.** Material ou serviço existia aqui por um motivo só — recortar a
 * lista para o insumo que fosse se vincular, e recusar o vínculo cruzado. Sem o
 * vínculo, o tipo não decidia mais nada: o orçamento mistura linha de material e
 * linha de serviço de propósito, e não filtra por ele.
 */
export const criarItemOrcamentoSchema = z.object({
  codigo: codigoSchema,
  nome: nomeSchema,
  /** O que não cabe no nome: escopo, critério de medição, exclusões. */
  descricao: z.string().trim().max(1000, 'A descrição tem no máximo 1000 caracteres.').optional(),
  ativo: z.boolean().prefault(true),
});

export type CriarItemOrcamento = z.infer<typeof criarItemOrcamentoSchema>;
export const atualizarItemOrcamentoSchema = criarItemOrcamentoSchema;
export type AtualizarItemOrcamento = z.infer<typeof atualizarItemOrcamentoSchema>;

export const itemOrcamentoSchema = z.object({
  id: uuidSchema,
  codigo: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ItemOrcamento = z.infer<typeof itemOrcamentoSchema>;

export const listaItensOrcamentoSchema = respostaListaSchema(itemOrcamentoSchema);
export type ListaItensOrcamento = z.infer<typeof listaItensOrcamentoSchema>;

/** Listagem: os filtros comuns de cadastro, e nada além deles. */
export const listarItensOrcamentoQuerySchema = listagemDeCadastroSchema;

export type ListarItensOrcamentoQuery = z.infer<typeof listarItensOrcamentoQuerySchema>;

/**
 * Colunas de ordenação.
 *
 * O padrão é `codigo`, crescente — e não `nome`, como nos outros cadastros. É a
 * diferença de um catálogo que o usuário codifica: numerado por etapa, o código
 * **é** a ordem do orçamento, e ordenar por nome embaralharia "FUND-01" com
 * "ALV-02" em ordem alfabética que não corresponde a obra nenhuma.
 */
export const COLUNAS_ORDENAVEIS_ITEM_ORCAMENTO = ['codigo', 'nome', 'createdAt'] as const;
